import React, { ReactNode, useEffect } from "react";
import { View, StyleSheet, Pressable, ViewStyle, StyleProp } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { useStorage } from "@/hooks/useStorage";
import { Skeleton, SkeletonCircle } from "@/components/ui/Skeleton";

export interface RemovableListItemProps<T> {
  items: T[];
  editing?: boolean;
  loading?: boolean;
  hoveredIndex: number | null;
  onHoverIn: (index: number) => void;
  onHoverOut: () => void;
  onRemove: (index: number) => void;
  renderContent: (item: T, index: number) => ReactNode;
  renderAction?: (item: T, index: number) => ReactNode;
  renderBadge?: (item: T, index: number) => ReactNode;
  isReadonly?: (item: T, index: number) => boolean;
  emptyMessage?: string;
  containerStyle?: StyleProp<ViewStyle>;
  itemStyle?: StyleProp<ViewStyle>;
  // Loading skeleton configuration
  storageKey?: string;
  skeletonCount?: number;
  skeletonAvatar?: boolean;
  skeletonDescription?: boolean;
}

function SkeletonItem({
  showAvatar = false,
  showDescription = false,
  style,
}: {
  showAvatar?: boolean;
  showDescription?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const { colors } = useTheme();

  return (
    <View
      style={[
        styles.item,
        {
          backgroundColor: colors.card,
          borderColor: colors.border,
        },
        style,
      ]}
    >
      {showAvatar && <SkeletonCircle size={32} />}
      <View style={{ flex: 1, gap: showDescription ? 6 : 0 }}>
        <Skeleton width="60%" height={14} />
        {showDescription && <Skeleton width="40%" height={10} />}
      </View>
    </View>
  );
}

export function RemovableListItem<T>({
  items,
  editing = false,
  loading = false,
  hoveredIndex,
  onHoverIn,
  onHoverOut,
  onRemove,
  renderContent,
  renderAction,
  renderBadge,
  isReadonly,
  emptyMessage = "No items",
  containerStyle,
  itemStyle,
  storageKey,
  skeletonCount,
  skeletonAvatar = false,
  skeletonDescription = false,
}: RemovableListItemProps<T>) {
  const { colors } = useTheme();
  const { getNumber, saveFast } = useStorage();

  // Get stored count for skeleton, default to 1
  const storedCount = storageKey ? (getNumber(`list-items-${storageKey}`) ?? 1) : 1;
  const displaySkeletonCount = skeletonCount ?? storedCount;

  // Persist item count when items change (and not loading)
  useEffect(() => {
    if (storageKey && !loading && items.length > 0) {
      saveFast(`list-items-${storageKey}`, items.length);
    }
  }, [storageKey, items.length, loading, saveFast]);

  // Show skeletons when loading
  if (loading) {
    return (
      <View style={[styles.container, containerStyle]}>
        {Array.from({ length: displaySkeletonCount }).map((_, index) => (
          <SkeletonItem
            key={index}
            showAvatar={skeletonAvatar}
            showDescription={skeletonDescription}
            style={itemStyle}
          />
        ))}
      </View>
    );
  }

  if (!items || items.length === 0) {
    return <ThemedText style={{ opacity: 0.5 }}>{emptyMessage}</ThemedText>;
  }

  return (
    <View style={[styles.container, containerStyle]}>
      {items.map((item, index) => {
        const readonly = isReadonly?.(item, index) ?? false;

        return (
          <View
            key={index}
            onPointerEnter={() => onHoverIn(index)}
            onPointerLeave={onHoverOut}
            style={[
              styles.item,
              {
                backgroundColor: colors.card,
                borderColor: colors.border,
              },
              readonly && {
                opacity: 0.7,
              },
              itemStyle,
            ]}
          >
            {renderContent(item, index)}
            {renderBadge && renderBadge(item, index)}
            {renderAction && renderAction(item, index)}
            {!readonly && (
              <Pressable
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onRemove(index);
                }}
                style={{
                  padding: 4,
                  marginLeft: 4,
                  opacity: editing ? 1 : 0,
                  pointerEvents: editing ? "auto" : "none",
                }}
              >
                {index === hoveredIndex && (
                  <MaterialCommunityIcons name="close-circle" size={20} color={colors.textSecondary} />
                )}
              </Pressable>
            )}
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  item: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
});
